import React, { useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, XCircle, CheckCircle, Info, X } from "lucide-react";
import "./ConfirmModal.scss";

/**
 * AlertModal — Info/error dialog with a single OK/close button.
 * Reuses ConfirmModal styles. Renders via portal at document.body.
 *
 * Props:
 *   show      — visibility flag
 *   onClose   — close handler
 *   title     — modal title
 *   message   — body text (string or ReactNode)
 *   buttonText — button label (default "OK")
 *   variant   — "danger" | "warning" | "success" | "info" (default "danger")
 *   icon      — custom icon element (auto-selected from variant if omitted)
 */

const VARIANT_ICONS = {
  danger: <XCircle size={22} />,
  warning: <AlertTriangle size={22} />,
  success: <CheckCircle size={22} />,
  info: <Info size={22} />,
};

const AlertModal = ({
  show,
  onClose,
  title = "Error",
  message = "Something went wrong.",
  buttonText = "OK",
  variant = "danger",
  icon,
}) => {
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape") onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (show) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [show, handleKeyDown]);

  if (!show) return null;

  const displayIcon = icon || VARIANT_ICONS[variant] || VARIANT_ICONS.danger;

  return createPortal(
    <div className="confirm-overlay" onClick={onClose}>
      <div
        className="confirm-dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-msg"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="confirm-dialog__close"
          onClick={onClose}
          aria-label="Close"
        >
          <X size={16} />
        </button>

        <div className={`confirm-dialog__icon confirm-dialog__icon--${variant}`}>
          {displayIcon}
        </div>

        <h3 className="confirm-dialog__title" id="alert-dialog-title">
          {title}
        </h3>
        <div className="confirm-dialog__message" id="alert-dialog-msg">
          {message}
        </div>

        <div className="confirm-dialog__actions">
          <button
            className={`confirm-dialog__btn confirm-dialog__btn--${variant}`}
            onClick={onClose}
          >
            {buttonText}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AlertModal;
